import formatDistanceToNowStrict from 'date-fns/formatDistanceToNowStrict';

import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { getFormattedNumber } from '@lyearn/core/utils/number/getFormattedNumber';
import { getFormattedPercentage } from '@lyearn/core/utils/number/getFormattedPercentage';
import { getHumanReadableCount } from '@lyearn/core/utils/number/getHumanReadableCount';
import { getKNotationNumber } from '@lyearn/core/utils/number/getKNotationNumber';
import { getHumanDurationInHours } from '@/utils/time/getHumanDurationRounded';

import { formatDimensionDate, formatDimensionHTML, formatDimensionMonth } from '../dimension';

export const shortenTimeUnitLabel = (distanceToNowString: string) =>
  distanceToNowString
    .replace(' minutes', 'm')
    .replace(' seconds', 's')
    .replace(' hours', 'h')
    .replace(' days', 'd')
    .replace(' months', 'mo')
    .replace(' years', 'y')
    .replace(' minute', 'm')
    .replace(' second', 's')
    .replace(' hour', 'h')
    .replace(' day', 'd')
    .replace(' month', 'mo')
    .replace(' year', 'y');

const formatTimestamp = (value: number | string) =>
  value ? formatDateWithYear(value, DateFormat.DateTime) : '-';

const formatHTML = (value: number | string) => {
  if (typeof value === 'number') {
    return `${value}`;
  }
  return formatDimensionHTML(value);
};

const formatDateValue = (value: number | string) => {
  if (typeof value === 'number') {
    return `${value}`;
  }

  return formatDimensionDate(value);
};

const formatMonthValue = (value: number | string) => {
  if (typeof value === 'number') {
    return `${value}`;
  }

  return formatDimensionMonth(value);
};

const formatRelativeTimestamp = (value: number | string) =>
  `${shortenTimeUnitLabel(
    formatDistanceToNowStrict(typeof value === 'number' ? value : new Date(value)),
  )} ago`;

const formatSeconds = (value: number, formatZeroValue: boolean) =>
  value || formatZeroValue ? getHumanDurationInHours(value) : '-';

const VALUE_FORMATTERS = {
  html: formatHTML,
  percent: getFormattedPercentage,
  timestamp: formatTimestamp,
  date: formatDateValue,
  month: formatMonthValue,
  relativeTimestamp: formatRelativeTimestamp,
  seconds: formatSeconds,
  number: getFormattedNumber,
  default: (value: number) => getKNotationNumber(value, { uppercase: true }),
};

const TOOLTIP_FORMATTERS = {
  percent: getFormattedPercentage,
  timestamp: formatTimestamp,
  relativeTimestamp: formatRelativeTimestamp,
  seconds: getHumanDurationInHours,
  default: getHumanReadableCount,
};

export const AXIS_WIDTH = {
  seconds: 60,
  default: 40,
  timestamp: 80,
};

export const getFormattedValue = (
  format: string | undefined,
  value: number,
  formatZeroValue: boolean = true,
) => {
  const formatter = VALUE_FORMATTERS[format as keyof typeof VALUE_FORMATTERS];
  return formatter ? formatter(value, formatZeroValue) : VALUE_FORMATTERS.default(value);
};

export const getFormattedTooltipValue = (format: string | undefined, value: number) => {
  const formatter = TOOLTIP_FORMATTERS[format as keyof typeof TOOLTIP_FORMATTERS];
  return formatter ? formatter(value) : TOOLTIP_FORMATTERS.default(value);
};

export const getAxisWidth = (format: string | undefined) => {
  const width = AXIS_WIDTH[format as keyof typeof AXIS_WIDTH];
  return width ? width : AXIS_WIDTH.default;
};
